import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Archive, Check, Copy, History, Lightbulb, Pencil, Plus, RotateCcw, Search, X } from 'lucide-react';
import axiosInstance from '../utils/axiosInstance';
import { Md, Mermaid, timeAgo } from '../components/bible/rich';
import V2Shell from './V2Shell';

const API = 'api/replai/bible';

const SECTION_ORDER = [
  { kind: 'rule', label: 'Rules' },
  { kind: 'diagram', label: 'Flow' },
  { kind: 'steps', label: 'Step by step' },
  { kind: 'wording', label: 'Approved wording' },
  { kind: 'source', label: 'Where to look' },
  { kind: 'question', label: 'Open questions' },
  { kind: 'note', label: 'Notes' },
];

const litShare = (approved, total) => (total ? Math.round((approved / total) * 100) : 0);

const LitMeter = ({ share, className = '' }) => (
  <div className={`lit-meter ${className}`} role="img" aria-label={`${share}% approved`}>
    <span style={{ width: `${share}%` }} />
  </div>
);

const IconButton = ({ label, onClick, children, tone = '' }) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-[0.8rem] font-medium text-[var(--ink-2)] hover:bg-[var(--paper)] hover:text-[var(--ink)] ${tone}`}
  >
    {children}
    {label}
  </button>
);

/* ------------------------------------------------------------ edit + history */

const Editor = ({ block, isNew, slug, onDone }) => {
  const [title, setTitle] = useState(block.title || '');
  const [body, setBody] = useState(block.body || '');
  const [lang, setLang] = useState(block.lang || '');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const withTitle = ['steps', 'diagram', 'source', 'note'].includes(block.kind);

  const save = async () => {
    setBusy(true);
    setError('');
    const payload = { title, body, lang, change_note: note };
    try {
      if (isNew) await axiosInstance.post(`${API}/chapters/${slug}/blocks/`, { ...payload, kind: block.kind });
      else await axiosInstance.patch(`${API}/blocks/${block.id}/`, payload);
      onDone(true);
    } catch {
      setError('Not saved. Check your connection and try again.');
      setBusy(false);
    }
  };

  const field = 'w-full rounded-md border border-[var(--line)] bg-[var(--surface)] px-3 py-2 text-[0.95rem] focus:border-[var(--ink-2)] focus:outline-none';
  return (
    <div className="space-y-2.5 py-2">
      {withTitle && <input className={field} placeholder="Title" value={title} onChange={(e) => setTitle(e.target.value)} />}
      <textarea
        autoFocus
        className={`${field} min-h-[6rem] ${block.kind === 'diagram' ? 'font-mono text-xs' : ''}`}
        rows={block.kind === 'diagram' || block.kind === 'steps' ? 10 : 3}
        value={body}
        onChange={(e) => setBody(e.target.value)}
      />
      {block.kind === 'diagram' && body.trim() && (
        <div className="rounded-md border border-dashed border-[var(--line)] bg-[var(--surface)] p-3"><Mermaid source={body} /></div>
      )}
      {block.kind === 'wording' && (
        <select className={`${field} w-28`} value={lang} onChange={(e) => setLang(e.target.value)}>
          {['', 'en', 'nl', 'fr', 'de', 'es', 'it', 'da'].map((l) => <option key={l} value={l}>{l ? l.toUpperCase() : 'Language'}</option>)}
        </select>
      )}
      <input className={field} placeholder="What changed, and why? Shown in the history." value={note} onChange={(e) => setNote(e.target.value)} />
      {error && <p className="text-sm text-red-700">{error}</p>}
      <div className="flex gap-2">
        <button type="button" disabled={busy || !body.trim()} onClick={save} className="rounded-md bg-[var(--ink)] px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-40">
          {busy ? 'Saving' : isNew ? 'Add' : 'Save'}
        </button>
        <button type="button" onClick={() => onDone(false)} className="rounded-md px-3 py-1.5 text-sm text-[var(--ink-2)] hover:bg-[var(--paper)]">Cancel</button>
      </div>
    </div>
  );
};

const HistoryList = ({ block, onRestored }) => {
  const [revs, setRevs] = useState(null);
  useEffect(() => {
    axiosInstance.get(`${API}/blocks/${block.id}/revisions/`).then(({ data }) => setRevs(data)).catch(() => setRevs([]));
  }, [block.id, block.version]);
  const restore = async (r) => {
    await axiosInstance.post(`${API}/blocks/${block.id}/restore/`, { revision: r.id });
    onRestored();
  };
  if (!revs) return <p className="py-2 text-sm text-[var(--ink-3)]">Loading history</p>;
  return (
    <ol className="my-2 space-y-2 border-l border-[var(--line)] pl-4">
      {revs.map((r, i) => (
        <li key={r.id} className="text-sm">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="num font-semibold">v{r.version}</span>
            <span className="text-[var(--ink-2)]">{r.action} by {r.edited_by || 'unknown'}, {timeAgo(r.created_at)}</span>
            {i > 0 && (
              <button type="button" onClick={() => restore(r)} className="ml-auto inline-flex items-center gap-1 text-[0.8rem] font-medium text-[var(--ink-2)] hover:text-[var(--ink)]">
                <RotateCcw className="h-3 w-3" /> Restore this version
              </button>
            )}
          </div>
          {r.change_note && <p className="text-[var(--ink-2)]">&ldquo;{r.change_note}&rdquo;</p>}
          {i > 0 && <p className="mt-0.5 line-clamp-2 whitespace-pre-wrap text-[var(--ink-3)]">{r.body}</p>}
        </li>
      ))}
    </ol>
  );
};

/* ------------------------------------------------------------ one block */

const BlockRow = ({ block, refLabel, slug, onChanged, highlighted }) => {
  const [mode, setMode] = useState('view');
  const [deciding, setDeciding] = useState(false);
  const [decision, setDecision] = useState('');
  const [copied, setCopied] = useState(false);
  const ref = useRef(null);
  const lit = block.status === 'approved';

  useEffect(() => {
    if (highlighted && ref.current) ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [highlighted]);

  const patch = async (payload) => {
    await axiosInstance.patch(`${API}/blocks/${block.id}/`, payload);
    onChanged();
  };
  const archive = async () => {
    const note = window.prompt('Archive this block? Say why (optional):', '');
    if (note === null) return;
    await axiosInstance.delete(`${API}/blocks/${block.id}/`, { params: { note } });
    onChanged();
  };

  const body = (() => {
    switch (block.kind) {
      case 'diagram':
        return (
          <div className="rounded-lg border border-[var(--line)] bg-[var(--surface)] p-5">
            {block.title && <p className="mb-3 font-semibold">{block.title}</p>}
            <Mermaid source={block.body} />
          </div>
        );
      case 'steps':
        return (
          <div>
            {block.title && <p className="mb-1 font-semibold">{block.title}</p>}
            <Md className="prose-body [&_ol]:pl-6">{block.body}</Md>
          </div>
        );
      case 'wording':
        return <p className="prose-body">{block.body}</p>;
      case 'source':
        return (
          <div className="prose-body">
            {block.title && <span className="font-semibold">{block.title}. </span>}
            <span className="text-[var(--ink-2)]">{block.body}</span>
          </div>
        );
      case 'question':
        return (
          <div>
            <Md className={`prose-body ${block.resolved ? 'text-[var(--ink-3)]' : ''}`}>{block.body}</Md>
            {block.resolved && block.decision && (
              <p className="mt-1.5 text-[0.95rem]">
                <span className="font-semibold">Decided by {block.decision.by}:</span> {block.decision.note}
              </p>
            )}
            {deciding && (
              <div className="mt-2 space-y-2">
                <textarea
                  autoFocus
                  rows={2}
                  value={decision}
                  onChange={(e) => setDecision(e.target.value)}
                  placeholder="What did the team decide? Then update the rule it affects."
                  className="w-full rounded-md border border-[var(--line)] bg-[var(--surface)] px-3 py-2 text-[0.95rem] focus:outline-none"
                />
                <div className="flex gap-2">
                  <button type="button" disabled={!decision.trim()} onClick={() => patch({ resolved: true, change_note: decision })} className="rounded-md bg-[var(--ink)] px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-40">Record decision</button>
                  <button type="button" onClick={() => setDeciding(false)} className="rounded-md px-3 py-1.5 text-sm text-[var(--ink-2)]">Cancel</button>
                </div>
              </div>
            )}
          </div>
        );
      default:
        return (
          <div>
            <Md className="prose-body">{block.body}</Md>
            {block.sources?.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {block.sources.map((s) => (
                  <span key={s} className="rounded border border-[var(--line)] px-1.5 py-px text-[0.75rem] text-[var(--ink-2)]">{s}</span>
                ))}
              </div>
            )}
          </div>
        );
    }
  })();

  return (
    <li
      ref={ref}
      id={`b-${block.id}`}
      className={`group relative grid grid-cols-[3.25rem_minmax(0,1fr)] gap-x-3 border-t border-[var(--line-2)] py-4 ${highlighted ? 'bg-[var(--glow)]/60' : ''}`}
    >
      {lit && <span className="absolute -left-3 bottom-4 top-4 w-[3px] rounded-full bg-[var(--brass)]" aria-hidden />}
      <span className={`num pt-[0.2rem] text-[0.85rem] ${lit ? 'font-semibold text-[var(--brass)]' : 'text-[var(--ink-3)]'}`}>{refLabel}</span>
      <div className="min-w-0">
        {mode === 'edit' ? (
          <Editor block={block} slug={slug} onDone={(saved) => { setMode('view'); if (saved) onChanged(); }} />
        ) : body}
        {block.status === 'unconfirmed' && mode !== 'edit' && (
          <p className="mt-1.5 text-[0.8rem] text-[var(--ask)]">Unconfirmed: based on a single example. Check before relying on it.</p>
        )}
        {mode !== 'edit' && (
          <div className="mt-2 flex flex-wrap items-center gap-1 text-[0.8rem] text-[var(--ink-3)]">
            <span className="mr-1">
              {lit ? `Approved by ${block.updated_by}` : block.version > 1 ? `Edited by ${block.updated_by}` : 'Draft from tickets'}, {timeAgo(block.updated_at)}
            </span>
            <span className="flex flex-wrap items-center gap-0.5 opacity-100 transition-opacity md:opacity-0 md:group-focus-within:opacity-100 md:group-hover:opacity-100">
              {block.kind !== 'question' && (
                <IconButton label={lit ? 'Unapprove' : 'Approve'} onClick={() => patch({ status: lit ? 'draft' : 'approved', change_note: '' })}>
                  <Lightbulb className={`h-3.5 w-3.5 ${lit ? 'fill-[var(--brass)] text-[var(--brass)]' : ''}`} />
                </IconButton>
              )}
              {block.kind === 'question' && !block.resolved && !deciding && (
                <IconButton label="Decide" onClick={() => setDeciding(true)}><Check className="h-3.5 w-3.5" /></IconButton>
              )}
              {block.kind === 'question' && block.resolved && (
                <IconButton label="Reopen" onClick={() => patch({ resolved: false, change_note: 'reopened' })}><RotateCcw className="h-3.5 w-3.5" /></IconButton>
              )}
              {block.kind === 'wording' && (
                <IconButton label={copied ? 'Copied' : 'Copy'} onClick={() => { navigator.clipboard.writeText(block.body); setCopied(true); setTimeout(() => setCopied(false), 1500); }}>
                  <Copy className="h-3.5 w-3.5" />
                </IconButton>
              )}
              <IconButton label="Edit" onClick={() => setMode('edit')}><Pencil className="h-3.5 w-3.5" /></IconButton>
              <IconButton label="History" onClick={() => setMode(mode === 'history' ? 'view' : 'history')}><History className="h-3.5 w-3.5" /></IconButton>
              <IconButton label="Archive" onClick={archive}><Archive className="h-3.5 w-3.5" /></IconButton>
            </span>
          </div>
        )}
        {mode === 'history' && <HistoryList block={block} onRestored={() => { setMode('view'); onChanged(); }} />}
      </div>
    </li>
  );
};

/* ------------------------------------------------------------ chapter */

const Chapter = ({ slug, highlightId, onChanged }) => {
  const [chapter, setChapter] = useState(null);
  const [failed, setFailed] = useState(false);
  const [adding, setAdding] = useState(null);
  const [lang, setLang] = useState('all');

  const load = useCallback(() => {
    axiosInstance.get(`${API}/chapters/${slug}/`).then(({ data }) => { setChapter(data); setFailed(false); }).catch(() => setFailed(true));
  }, [slug]);
  useEffect(() => { setChapter(null); setLang('all'); load(); }, [load]);
  const changed = () => { load(); onChanged(); };

  if (failed) return <p className="text-[var(--ink-2)]">This chapter could not be loaded. Reload the page to try again.</p>;
  if (!chapter) return <div className="h-64 animate-pulse rounded-lg bg-[var(--line-2)]" />;

  const of = (k) => chapter.blocks.filter((b) => b.kind === k);
  const approvedCount = chapter.blocks.filter((b) => b.status === 'approved').length;
  const approvable = chapter.blocks.filter((b) => b.kind !== 'question').length;
  const share = litShare(approvedCount, approvable);
  const langs = [...new Set(of('wording').map((b) => b.lang).filter(Boolean))];

  const setStatus = async (status) => {
    await axiosInstance.patch(`${API}/chapters/${slug}/`, { status });
    changed();
  };

  return (
    <article className="reading">
      <p className="num text-[var(--ink-2)]">Chapter {chapter.number}</p>
      <h1 className="mt-1 text-[2.4rem] font-semibold leading-[1.1]">{chapter.title}</h1>
      <Md className="prose-body mt-4 text-[var(--ink-2)]">{chapter.summary}</Md>

      <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2">
        <LitMeter share={share} className="w-40" />
        <span className="num text-sm text-[var(--ink-2)]">{approvedCount} of {approvable} approved</span>
        {chapter.status === 'approved' ? (
          <button type="button" onClick={() => setStatus('draft')} className="ml-auto text-sm text-[var(--ink-2)] hover:text-[var(--ink)]">
            Signed off by {chapter.approved_by}. Undo
          </button>
        ) : (
          <button type="button" onClick={() => setStatus('approved')} className="ml-auto rounded-md border border-[var(--line)] bg-[var(--surface)] px-3 py-1.5 text-sm font-semibold hover:border-[var(--ink-2)]">
            Sign off chapter
          </button>
        )}
      </div>

      <nav className="mt-6 flex flex-wrap gap-x-4 gap-y-1 border-b border-[var(--line)] pb-3 text-[0.95rem]">
        {SECTION_ORDER.filter((s) => of(s.kind).length).map((s) => (
          <a key={s.kind} href={`#sec-${s.kind}`} className="text-[var(--ink-2)] hover:text-[var(--ink)]">
            {s.label} <span className="num text-[var(--ink-3)]">{of(s.kind).length}</span>
          </a>
        ))}
      </nav>

      {SECTION_ORDER.map(({ kind, label }) => {
        let blocks = of(kind);
        if (!blocks.length && kind === 'note') return null;
        if (kind === 'wording' && lang !== 'all') blocks = blocks.filter((b) => b.lang === lang);
        return (
          <section key={kind} id={`sec-${kind}`} className="scroll-mt-6 pt-10">
            <div className="mb-2 flex items-baseline justify-between gap-4">
              <h2 className="text-[1.35rem] font-semibold">{label}</h2>
              {kind === 'wording' && langs.length > 1 && (
                <div className="flex gap-1 text-sm">
                  {['all', ...langs].map((l) => (
                    <button key={l} type="button" onClick={() => setLang(l)} className={`rounded px-2 py-0.5 ${lang === l ? 'bg-[var(--ink)] text-white' : 'text-[var(--ink-2)] hover:bg-[var(--line-2)]'}`}>
                      {l === 'all' ? 'All' : l.toUpperCase()}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <ol className="pl-3">
              {blocks.map((b, i) => (
                <BlockRow
                  key={b.id}
                  block={b}
                  slug={slug}
                  highlighted={String(b.id) === highlightId}
                  onChanged={changed}
                  refLabel={kind === 'rule' ? `§${chapter.number}.${i + 1}` : kind === 'wording' ? (b.lang || '').toUpperCase() : kind === 'steps' || kind === 'question' ? `${i + 1}` : ''}
                />
              ))}
              {adding === kind && (
                <li className="border-t border-[var(--line-2)] pl-[3.25rem]">
                  <Editor block={{ kind }} isNew slug={slug} onDone={(saved) => { setAdding(null); if (saved) changed(); }} />
                </li>
              )}
            </ol>
            {adding !== kind && (
              <button type="button" onClick={() => setAdding(kind)} className="mt-1 inline-flex items-center gap-1 pl-[4rem] text-sm text-[var(--ink-3)] hover:text-[var(--ink)]">
                <Plus className="h-3.5 w-3.5" /> Add {label.toLowerCase().replace(/s$/, '')}
              </button>
            )}
          </section>
        );
      })}
    </article>
  );
};

/* ------------------------------------------------------------ overview */

const Overview = ({ chapters }) => {
  const [questions, setQuestions] = useState(null);
  const [activity, setActivity] = useState(null);
  useEffect(() => {
    axiosInstance.get(`${API}/questions/`).then(({ data }) => setQuestions(data)).catch(() => setQuestions([]));
    axiosInstance.get(`${API}/activity/`, { params: { humans: 1, limit: 12 } }).then(({ data }) => setActivity(data)).catch(() => setActivity([]));
  }, []);
  const approved = chapters.reduce((t, c) => t + (c.n_approved || 0), 0);
  const total = chapters.reduce((t, c) => t + (c.n_blocks || 0) - (c.n_open || 0), 0);
  const byChapter = useMemo(() => {
    const m = new Map();
    (questions || []).forEach((q) => { if (!m.has(q.chapter.slug)) m.set(q.chapter.slug, { chapter: q.chapter, items: [] }); m.get(q.chapter.slug).items.push(q); });
    return [...m.values()];
  }, [questions]);

  return (
    <article className="reading">
      <h1 className="text-[2.4rem] font-semibold leading-[1.1]">How LampTwist answers customers</h1>
      <p className="prose-body mt-4 text-[var(--ink-2)]">
        Our policies, processes and wording in one place. It was drafted from 6,957 real tickets, and from here on
        the team owns it: edit any rule, and every change is kept with your name. Replai follows only what is written
        here, so fixing a rule here fixes Replai&apos;s drafts too.
      </p>
      <div className="mt-6 flex items-center gap-4">
        <LitMeter share={litShare(approved, total)} className="w-56" />
        <span className="num text-sm text-[var(--ink-2)]">{approved} of {total} approved by the team</span>
      </div>

      <h2 className="mt-12 text-[1.35rem] font-semibold">Decisions the team needs to take</h2>
      <p className="mt-1 text-[var(--ink-2)]">
        Where our tickets contradict each other. Each decision settles a rule for everyone, Replai included.
      </p>
      {!questions ? <p className="mt-4 text-[var(--ink-3)]">Loading</p> : !questions.length ? (
        <p className="mt-4">No open questions. Every rule has an owner-approved answer.</p>
      ) : (
        <div className="mt-4 space-y-6">
          {byChapter.map(({ chapter, items }) => (
            <div key={chapter.slug}>
              <Link to={`/bible/${chapter.slug}#sec-question`} className="font-semibold hover:underline">
                {chapter.number}. {chapter.title}
              </Link>
              <ul className="mt-1.5 space-y-1.5">
                {items.slice(0, 3).map((q) => (
                  <li key={q.id}>
                    <Link to={`/bible/${chapter.slug}#b-${q.id}`} className="block text-[var(--ink-2)] hover:text-[var(--ink)]">{q.body}</Link>
                  </li>
                ))}
                {items.length > 3 && (
                  <li><Link to={`/bible/${chapter.slug}#sec-question`} className="text-sm text-[var(--ink-3)] hover:text-[var(--ink)]">{items.length - 3} more in this chapter</Link></li>
                )}
              </ul>
            </div>
          ))}
        </div>
      )}

      <h2 className="mt-12 text-[1.35rem] font-semibold">Recent changes by the team</h2>
      {!activity ? <p className="mt-3 text-[var(--ink-3)]">Loading</p> : !activity.length ? (
        <p className="mt-3 text-[var(--ink-2)]">No edits yet. Open a chapter and approve, edit or question a rule.</p>
      ) : (
        <ul className="mt-3 divide-y divide-[var(--line-2)]">
          {activity.map((r) => (
            <li key={r.id} className="py-2.5">
              <Link to={`/bible/${r.chapter.slug}#b-${r.block}`} className="block hover:text-[var(--ink)]">
                <span className="font-medium">{r.edited_by}</span>
                <span className="text-[var(--ink-2)]"> {r.action} a {r.kind} in {r.chapter.title}, {timeAgo(r.created_at)}</span>
                {r.change_note && <span className="block text-sm text-[var(--ink-2)]">&ldquo;{r.change_note}&rdquo;</span>}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </article>
  );
};

/* ------------------------------------------------------------ search */

const SearchDialog = ({ onClose }) => {
  const [q, setQ] = useState('');
  const [hits, setHits] = useState([]);
  const [active, setActive] = useState(0);
  const navigate = useNavigate();
  useEffect(() => {
    if (q.trim().length < 3) { setHits([]); return undefined; }
    const t = setTimeout(() => {
      axiosInstance.get(`${API}/search/`, { params: { q } }).then(({ data }) => { setHits(data); setActive(0); }).catch(() => setHits([]));
    }, 200);
    return () => clearTimeout(t);
  }, [q]);
  const go = (h) => { onClose(); navigate(`/bible/${h.chapter.slug}#b-${h.id}`); };
  const onKey = (e) => {
    if (e.key === 'Escape') onClose();
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, hits.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    if (e.key === 'Enter' && hits[active]) go(hits[active]);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-[rgba(31,38,48,0.28)] px-4 pt-[12vh]" onMouseDown={onClose}>
      <div className="w-full max-w-2xl overflow-hidden rounded-xl border border-[var(--line)] bg-[var(--surface)] shadow-[0_24px_60px_rgba(31,38,48,0.18)]" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 border-b border-[var(--line)] px-4">
          <Search className="h-4 w-4 text-[var(--ink-3)]" />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKey} placeholder="Search rules, wording and processes, in English" className="w-full bg-transparent py-4 text-[1.05rem] focus:outline-none" />
          <button type="button" onClick={onClose} aria-label="Close search"><X className="h-4 w-4 text-[var(--ink-3)]" /></button>
        </div>
        <ul className="max-h-[55vh] overflow-y-auto p-2">
          {q.trim().length >= 3 && !hits.length && <li className="px-3 py-3 text-[var(--ink-2)]">Nothing matches. Try other words, or another language.</li>}
          {hits.map((h, i) => (
            <li key={h.id}>
              <button type="button" onMouseEnter={() => setActive(i)} onClick={() => go(h)} className={`w-full rounded-md px-3 py-2.5 text-left ${i === active ? 'bg-[var(--paper)]' : ''}`}>
                <span className="block text-[0.8rem] text-[var(--ink-3)]">{h.chapter.number}. {h.chapter.title}</span>
                <span className="line-clamp-2">{h.title ? `${h.title}. ` : ''}{h.body}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------ page */

const BibleV2 = () => {
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [chapters, setChapters] = useState(null);
  const [searching, setSearching] = useState(false);
  const highlightId = location.hash.startsWith('#b-') ? location.hash.slice(3) : null;

  const loadChapters = useCallback(() => {
    axiosInstance.get(`${API}/chapters/`).then(({ data }) => setChapters(data)).catch(() => setChapters([]));
  }, []);
  useEffect(() => { loadChapters(); }, [loadChapters]);
  useEffect(() => {
    const onKey = (e) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setSearching(true); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <V2Shell>
      <div className="grid min-h-screen grid-cols-1 lg:grid-cols-[17rem_minmax(0,1fr)]">
        <aside className={`${slug ? 'hidden lg:block' : ''} border-b border-[var(--line)] px-5 py-6 lg:sticky lg:top-0 lg:h-screen lg:overflow-y-auto lg:border-b-0 lg:border-r`}>
          <button type="button" onClick={() => setSearching(true)} className="flex w-full items-center gap-2 rounded-md border border-[var(--line)] bg-[var(--surface)] px-3 py-2 text-left text-[var(--ink-3)] hover:border-[var(--ink-3)]">
            <Search className="h-4 w-4" /> <span className="flex-1">Search</span> <kbd className="text-[0.75rem]">⌘K</kbd>
          </button>
          <button type="button" onClick={() => navigate('/bible')} className={`mt-5 block w-full rounded-md px-2 py-1.5 text-left ${!slug ? 'bg-[var(--surface)] font-semibold shadow-[inset_0_0_0_1px_var(--line)]' : 'text-[var(--ink-2)] hover:text-[var(--ink)]'}`}>
            Overview
          </button>
          <ol className="mt-2 space-y-0.5">
            {(chapters || []).map((c) => {
              const share = litShare(c.n_approved || 0, (c.n_blocks || 0) - (c.n_open || 0));
              const active = c.slug === slug;
              return (
                <li key={c.slug}>
                  <button type="button" onClick={() => navigate(`/bible/${c.slug}`)} className={`w-full rounded-md px-2 py-2 text-left ${active ? 'bg-[var(--surface)] shadow-[inset_0_0_0_1px_var(--line)]' : 'hover:bg-[var(--surface)]'}`}>
                    <span className="flex gap-2 text-[0.95rem] leading-snug">
                      <span className="num w-5 shrink-0 text-[var(--ink-3)]">{c.number}</span>
                      <span className={active ? 'font-semibold' : 'text-[var(--ink-2)]'}>{c.title}</span>
                    </span>
                    <span className="mt-1.5 flex items-center gap-2 pl-7">
                      <LitMeter share={share} className="flex-1" />
                      {c.n_open > 0 && <span className="num text-[0.75rem] text-[var(--ask)]">{c.n_open} open</span>}
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        </aside>
        <div className="px-6 py-10 sm:px-12 lg:px-16">
          {!chapters ? (
            <div className="reading h-64 animate-pulse rounded-lg bg-[var(--line-2)]" />
          ) : slug ? (
            <>
              <Link to="/bible" className="mb-6 inline-block text-[var(--ink-2)] hover:text-[var(--ink)] lg:hidden">All chapters</Link>
              <Chapter slug={slug} highlightId={highlightId} onChanged={loadChapters} />
            </>
          ) : (
            <Overview chapters={chapters} />
          )}
        </div>
      </div>
      {searching && <SearchDialog onClose={() => setSearching(false)} />}
    </V2Shell>
  );
};

export default BibleV2;
